// "Pending Loans" (see pending_loans.py::build_pending_loans_report()):
// LoanTape rows with no cashflow record yet as of the reporting cutoff. The
// user ticks which of these should be treated as excluded (server persists
// via PUT .../pending-loans/selection - the FULL new selection, not a
// delta). Local draft Set vs. last-saved Set gives dirty tracking so "Save
// Selection" only lights up once something actually changed.

import React, { useEffect, useState, useCallback } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { readErrorDetail } from '../jobs';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import LoadingState from '../../common/LoadingState';
import EmptyState from '../../common/EmptyState';
import errorMessage from '../../../helpers/errorMessage';

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('default', { year: 'numeric', month: 'short', day: 'numeric' }) : '');
const formatAmount = (n) => (n === null || n === undefined ? '' : n.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const sameSet = (a, b) => a.size === b.size && [...a].every((k) => b.has(k));

const PendingLoansPanel = ({ reportingId, setLoading, onChange }) => {
  const [report, setReport] = useState(null);
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [draftKeys, setDraftKeys] = useState(new Set());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const applyReport = (data) => {
    setReport(data);
    const excluded = new Set((data.loans || []).filter((l) => l.excluded).map((l) => l.investment_key));
    setSavedKeys(excluded);
    setDraftKeys(new Set(excluded));
  };

  const fetchPendingLoans = useCallback(async () => {
    if (!reportingId) return;
    setLoading(true);
    try {
      const res = await axiosWithAuth.get(`/compartment-report/reportings/${reportingId}/pending-loans`);
      applyReport(res.data);
      setError(null);
    } catch (err) {
      console.error('[PendingLoansPanel] fetch error:', errorMessage(err));
      setError(readErrorDetail(err, 'Failed to load pending loans.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingId]);

  useEffect(() => {
    fetchPendingLoans();
  }, [fetchPendingLoans]);

  const toggleKey = (key, checked) => {
    setDraftKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const selectAll = () => setDraftKeys(new Set((report?.loans || []).map((l) => l.investment_key)));
  const selectNone = () => setDraftKeys(new Set());

  const dirty = !sameSet(savedKeys, draftKeys);

  const handleSave = async () => {
    if (!reportingId || !dirty) return;
    setSaving(true);
    setLoading(true);
    try {
      const res = await axiosWithAuth.put(`/compartment-report/reportings/${reportingId}/pending-loans/selection`, {
        investment_keys: Array.from(draftKeys),
      });
      applyReport(res.data);
      setError(null);
      if (onChange) onChange(res.data);
    } catch (err) {
      console.error('[PendingLoansPanel] save error:', errorMessage(err));
      setError(errorMessage(err, 'Failed to save pending loans selection.'));
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  if (!report) return error ? <div className="ex-inline-error">{error}</div> : <LoadingState label="Loading pending loans" />;

  return (
    <div>
      <div className="cr-summary-line">
        <span>
          {report.total_count} pending loan(s) - {formatAmount(report.total_amount)} total
        </span>
        {Object.entries(report.by_platform || {}).map(([platform, bucket]) => (
          <Tag key={platform} severity="info" value={`${platform}: ${bucket.count} (${formatAmount(bucket.amount)})`} />
        ))}
      </div>

      {error && <div className="ex-inline-error mb-3">{error}</div>}

      {report.loans.length === 0 ? (
        <EmptyState icon="pi-check-circle" title="No pending loans on this reporting." compact />
      ) : (
        <>
          <div className="flex gap-2 mb-2">
            <Button label="Select all" className="p-button-outlined p-button-sm" onClick={selectAll} />
            <Button label="Select none" className="p-button-outlined p-button-sm" onClick={selectNone} />
          </div>
          <DataTable value={report.loans} dataKey="investment_key" size="small" showGridlines stripedRows paginator rows={15}>
            <Column
              header="Exclude"
              style={{ width: '5rem' }}
              body={(row) => (
                <Checkbox
                  checked={draftKeys.has(row.investment_key)}
                  onChange={(e) => toggleKey(row.investment_key, e.checked)}
                />
              )}
            />
            <Column field="investment_key" header="Investment Key" />
            <Column field="source_platform_name" header="Originator" />
            <Column header="Invested Date" bodyClassName="cr-nowrap-cell" body={(row) => formatDate(row.invested_date)} />
            <Column header="Investment Amount" headerClassName="cr-num" bodyClassName="cr-num" body={(row) => formatAmount(row.investment_amount)} />
          </DataTable>
        </>
      )}

      <div className="flex flex-wrap align-items-center gap-3 mt-3">
        <Button label="Save Selection" onClick={handleSave} disabled={!dirty || saving} loading={saving} />
        {dirty ? (
          <Tag severity="warning" value={`Unsaved changes (${draftKeys.size} selected)`} />
        ) : (
          <Tag severity="success" value={`Saved (${savedKeys.size} selected)`} />
        )}
      </div>
    </div>
  );
};

export default PendingLoansPanel;
